import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '@/services/api'
import {
  formatPrice,
  formatDate,
  STATUS_LABELS_FR,
  SERVICE_CATEGORY_LABELS,
  PRODUCT_CATEGORY_LABELS,
  statusBadgeVariant,
} from '@/lib/format'
import type { Service, Product, Request as Req, Quote, Order, Review, User } from '@/lib/types'
import { ImageUpload } from '@/components/ui/image-upload'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Select } from '@/components/ui/select'

function PageHeader({
  title,
  eyebrow,
  action,
}: {
  title: string
  eyebrow?: string
  action?: ReactElement
}): ReactElement {
  return (
    <div className="mb-8 flex items-end justify-between">
      <div>
        {eyebrow && <p className="text-xs uppercase tracking-[0.3em] text-accent">{eyebrow}</p>}
        <h1 className="font-display mt-1 text-4xl text-primary">{title}</h1>
      </div>
      {action}
    </div>
  )
}

function StatusBadge({ status }: { status: string }): ReactElement {
  return <Badge variant={statusBadgeVariant(status)}>{STATUS_LABELS_FR[status] ?? status}</Badge>
}

// =============================================================================
// Dashboard
// =============================================================================
export function AdminDashboard(): ReactElement {
  const [data, setData] = useState({ requests: [] as Req[], orders: [] as Order[] })

  useEffect(() => {
    void Promise.all([
      api.get<{ data: Req[] }>('/requests'),
      api.get<{ data: Order[] }>('/orders'),
    ]).then(([r, o]) => {
      setData({ requests: r.data.data, orders: o.data.data })
    })
  }, [])

  const stats = useMemo(
    () => ({
      requests: data.requests.length,
      pending: data.requests.filter((r) => r.status === 'PENDING').length,
      orders: data.orders.length,
      revenue: data.orders
        .filter((o) => ['PAID', 'IN_PROGRESS', 'DELIVERED', 'COMPLETED'].includes(o.status))
        .reduce((sum, o) => sum + parseFloat(o.amount), 0),
    }),
    [data],
  )

  return (
    <div>
      <PageHeader eyebrow="Back-office MyPivot" title="Tableau de bord" />
      <div className="grid gap-4 md:grid-cols-4">
        <Stat label="Demandes" value={stats.requests} />
        <Stat label="À traiter" value={stats.pending} />
        <Stat label="Commandes" value={stats.orders} />
        <Stat label="Chiffre d'affaires" value={formatPrice(stats.revenue)} />
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Demandes récentes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 py-2">
            {data.requests.slice(0, 6).map((r) => (
              <Link
                key={r.id}
                to={`/admin/requests/${r.id}`}
                className="flex items-center justify-between rounded-md px-2 py-2 hover:bg-muted"
              >
                <div>
                  <div className="text-sm font-medium">{r.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {r.client?.name} — {formatDate(r.createdAt)}
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </Link>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Commandes en cours</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 py-2">
            {data.orders.slice(0, 6).map((o) => (
              <Link
                key={o.id}
                to={`/admin/orders/${o.id}`}
                className="flex items-center justify-between rounded-md px-2 py-2 hover:bg-muted"
              >
                <div>
                  <div className="text-sm font-medium">{o.request?.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {o.client?.name} — {formatPrice(o.amount, o.currency)}
                  </div>
                </div>
                <StatusBadge status={o.status} />
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string | number }): ReactElement {
  return (
    <Card>
      <CardContent className="py-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="font-display mt-2 text-3xl text-primary">{value}</div>
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Requests list + detail with quote builder
// =============================================================================
export function AdminRequests(): ReactElement {
  const [items, setItems] = useState<Req[]>([])
  useEffect(() => {
    void api.get<{ data: Req[] }>('/requests').then((r) => {
      setItems(r.data.data)
    })
  }, [])

  return (
    <div>
      <PageHeader eyebrow="Conciergerie" title="Demandes clients" />
      <Card>
        <table className="w-full">
          <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Client</th>
              <th className="px-6 py-3">Titre</th>
              <th className="px-6 py-3">Date</th>
              <th className="px-6 py-3">Statut</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-border/50 last:border-0 hover:bg-muted/40">
                <td className="px-6 py-3 text-sm">{r.client?.name}</td>
                <td className="px-6 py-3 text-sm font-medium">{r.title}</td>
                <td className="px-6 py-3 text-sm text-muted-foreground">
                  {formatDate(r.createdAt)}
                </td>
                <td className="px-6 py-3">
                  <StatusBadge status={r.status} />
                </td>
                <td className="px-6 py-3 text-right">
                  <Link
                    to={`/admin/requests/${r.id}`}
                    className="text-sm text-primary hover:underline"
                  >
                    Ouvrir →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

export function AdminRequestDetail(): ReactElement {
  const { id } = useParams<{ id: string }>()
  const [req, setReq] = useState<(Req & { quotes?: Quote[] }) | null>(null)
  const [services, setServices] = useState<Service[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [items, setItems] = useState<
    { label: string; unitPrice: number; quantity: number; description?: string }[]
  >([])
  const [taxRate, setTaxRate] = useState(0.2)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)

  function reload() {
    if (!id) return
    void api.get<{ data: Req & { quotes?: Quote[] } }>(`/requests/${id}`).then((r) => {
      setReq(r.data.data)
    })
  }
  useEffect(reload, [id])
  useEffect(() => {
    void api.get<{ data: Service[] }>('/services').then((r) => {
      setServices(r.data.data)
    })
    void api.get<{ data: Product[] }>('/products').then((r) => {
      setProducts(r.data.data)
    })
  }, [])

  function addItem() {
    setItems([...items, { label: '', unitPrice: 0, quantity: 1 }])
  }
  function addFromCatalog(kind: 'service' | 'product', refId: string) {
    if (kind === 'service') {
      const s = services.find((x) => x.id === refId)
      if (s)
        setItems([...items, { label: s.name, unitPrice: parseFloat(s.basePrice), quantity: 1 }])
    } else {
      const p = products.find((x) => x.id === refId)
      if (p) setItems([...items, { label: p.name, unitPrice: parseFloat(p.price), quantity: 1 }])
    }
  }
  function updateItem(i: number, patch: Partial<(typeof items)[number]>) {
    setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))
  }
  function removeItem(i: number) {
    setItems(items.filter((_, idx) => idx !== i))
  }

  const subtotal = items.reduce((s, it) => s + it.unitPrice * it.quantity, 0)
  const taxAmount = subtotal * taxRate
  const total = subtotal + taxAmount

  async function sendQuote(e: FormEvent) {
    e.preventDefault()
    if (!id) return
    setBusy(true)
    try {
      await api.post('/quotes', {
        requestId: id,
        items: items.filter((it) => it.label && it.unitPrice >= 0),
        taxRate,
        ...(notes ? { notes } : {}),
      })
      setItems([])
      setNotes('')
      reload()
    } finally {
      setBusy(false)
    }
  }

  if (!req) return <div className="text-muted-foreground">Chargement…</div>

  return (
    <div>
      <PageHeader
        eyebrow={`Client : ${req.client?.name}`}
        title={req.title}
        action={<StatusBadge status={req.status} />}
      />
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          <Card>
            <CardContent className="space-y-3 py-6">
              <h3 className="font-medium">Description</h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{req.description}</p>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                {req.location && (
                  <div>
                    <dt className="text-xs uppercase tracking-widest text-muted-foreground">
                      Lieu
                    </dt>
                    <dd>{req.location}</dd>
                  </div>
                )}
                {req.guestCount && (
                  <div>
                    <dt className="text-xs uppercase tracking-widest text-muted-foreground">
                      Invités
                    </dt>
                    <dd>{req.guestCount}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs uppercase tracking-widest text-muted-foreground">Début</dt>
                  <dd>{formatDate(req.startDate)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-muted-foreground">Fin</dt>
                  <dd>{formatDate(req.endDate)}</dd>
                </div>
              </dl>
              <div className="border-t border-border pt-3 text-xs">
                <strong className="text-muted-foreground">Contact :</strong> {req.client?.email}
                {req.client?.phone && ` · ${req.client.phone}`}
              </div>
            </CardContent>
          </Card>

          {req.quotes && req.quotes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Devis émis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 py-2">
                {req.quotes.map((q) => (
                  <div key={q.id} className="space-y-1 rounded border border-border p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{formatPrice(q.total, q.currency)}</span>
                      <StatusBadge status={q.status} />
                    </div>
                    <div className="space-y-0.5 text-xs text-muted-foreground">
                      {q.items?.map((it) => (
                        <div key={it.id} className="flex justify-between">
                          <span>
                            {it.label} × {it.quantity}
                          </span>
                          <span>{formatPrice(it.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Nouveau devis</CardTitle>
          </CardHeader>
          <form onSubmit={(e) => void sendQuote(e)}>
            <CardContent className="space-y-3 py-2">
              <div className="flex gap-2 text-xs">
                <Select
                  onChange={(e) => {
                    if (e.target.value) {
                      addFromCatalog('service', e.target.value)
                      e.target.value = ''
                    }
                  }}
                >
                  <option value="">+ Service</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
                <Select
                  onChange={(e) => {
                    if (e.target.value) {
                      addFromCatalog('product', e.target.value)
                      e.target.value = ''
                    }
                  }}
                >
                  <option value="">+ Produit</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[1fr_70px_70px_28px] items-center gap-2">
                    <Input
                      value={it.label}
                      onChange={(e) => {
                        updateItem(i, { label: e.target.value })
                      }}
                      placeholder="Libellé"
                    />
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={it.unitPrice}
                      onChange={(e) => {
                        updateItem(i, { unitPrice: parseFloat(e.target.value) })
                      }}
                    />
                    <Input
                      type="number"
                      min={1}
                      value={it.quantity}
                      onChange={(e) => {
                        updateItem(i, { quantity: parseInt(e.target.value, 10) || 1 })
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        removeItem(i)
                      }}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addItem}
                  className="w-full"
                >
                  + Ligne libre
                </Button>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="taxRate">TVA</Label>
                <Input
                  id="taxRate"
                  type="number"
                  min={0}
                  max={1}
                  step="0.01"
                  value={taxRate}
                  onChange={(e) => {
                    setTaxRate(parseFloat(e.target.value))
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="notes">Notes (interne / client)</Label>
                <Textarea
                  id="notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => {
                    setNotes(e.target.value)
                  }}
                />
              </div>
              <div className="space-y-1 border-t border-border pt-3 text-sm">
                <div className="flex justify-between">
                  <span>Sous-total</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>TVA ({Math.round(taxRate * 100)}%)</span>
                  <span>{formatPrice(taxAmount)}</span>
                </div>
                <div className="flex justify-between border-t border-border pt-2 font-display text-xl text-primary">
                  <span>Total</span>
                  <span>{formatPrice(total)}</span>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={busy || items.length === 0}>
                {busy ? 'Envoi…' : 'Envoyer le devis'}
              </Button>
            </CardContent>
          </form>
        </Card>
      </div>
    </div>
  )
}

// =============================================================================
// Orders
// =============================================================================
export function AdminOrders(): ReactElement {
  const [items, setItems] = useState<Order[]>([])
  function reload() {
    void api.get<{ data: Order[] }>('/orders').then((r) => {
      setItems(r.data.data)
    })
  }
  useEffect(reload, [])

  async function updateStatus(orderId: string, status: string) {
    await api.patch(`/orders/${orderId}/status`, { status })
    reload()
  }

  const STATUSES = [
    'PENDING_PAYMENT',
    'PAID',
    'IN_PROGRESS',
    'DELIVERED',
    'COMPLETED',
    'CANCELLED',
    'REFUNDED',
  ]

  return (
    <div>
      <PageHeader eyebrow="Provisioning" title="Commandes" />
      <Card>
        <table className="w-full">
          <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Client</th>
              <th className="px-6 py-3">Demande</th>
              <th className="px-6 py-3">Montant</th>
              <th className="px-6 py-3">Statut</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} className="border-b border-border/50 last:border-0">
                <td className="px-6 py-3 text-sm">{o.client?.name}</td>
                <td className="px-6 py-3 text-sm font-medium">{o.request?.title}</td>
                <td className="px-6 py-3 text-sm">{formatPrice(o.amount, o.currency)}</td>
                <td className="px-6 py-3">
                  <StatusBadge status={o.status} />
                </td>
                <td className="px-6 py-3 text-right">
                  <Select
                    className="inline-flex w-auto"
                    value={o.status}
                    onChange={(e) => void updateStatus(o.id, e.target.value)}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABELS_FR[s]}
                      </option>
                    ))}
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

// =============================================================================
// Clients
// =============================================================================
export function AdminClients(): ReactElement {
  // Derive a client list from the requests endpoint (admin view returns all
  // requests including client info) until a /clients endpoint is exposed.
  const [requests, setRequests] = useState<Req[]>([])
  useEffect(() => {
    void api.get<{ data: Req[] }>('/requests').then((r) => {
      setRequests(r.data.data)
    })
  }, [])

  const clients = useMemo(() => {
    const map = new Map<string, { user: NonNullable<Req['client']>; count: number }>()
    for (const r of requests) {
      if (!r.client) continue
      const existing = map.get(r.client.id)
      if (existing) existing.count += 1
      else map.set(r.client.id, { user: r.client, count: 1 })
    }
    return Array.from(map.values())
  }, [requests])

  return (
    <div>
      <PageHeader eyebrow="Carnet" title="Clients" />
      <Card>
        <table className="w-full">
          <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Nom</th>
              <th className="px-6 py-3">Email</th>
              <th className="px-6 py-3">Téléphone</th>
              <th className="px-6 py-3">Demandes</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.user.id} className="border-b border-border/50 last:border-0">
                <td className="px-6 py-3 text-sm font-medium">{c.user.name}</td>
                <td className="px-6 py-3 text-sm">{c.user.email}</td>
                <td className="px-6 py-3 text-sm text-muted-foreground">{c.user.phone ?? '—'}</td>
                <td className="px-6 py-3 text-sm">{c.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

// =============================================================================
// Services & Products CRUD (simple)
// =============================================================================
function CatalogCrud<
  TItem extends {
    id: string
    slug: string
    name: string
    description: string
    category: string
    imageUrl: string
    isActive: boolean
  },
>({
  title,
  resource,
  categoryMap,
  priceField,
}: {
  title: string
  resource: 'services' | 'products'
  categoryMap: Record<string, string>
  priceField: 'basePrice' | 'price'
}): ReactElement {
  const [items, setItems] = useState<TItem[]>([])
  const [editing, setEditing] = useState<Partial<TItem> | null>(null)

  function reload() {
    void api.get<{ data: TItem[] }>(`/${resource}?activeOnly=false`).then((r) => {
      setItems(r.data.data)
    })
  }
  useEffect(reload, [])

  async function save(e: FormEvent) {
    e.preventDefault()
    if (!editing) return
    const payload: Record<string, unknown> = { ...editing }
    delete payload.id
    delete payload.createdAt
    delete payload.updatedAt
    if (editing.id) {
      await api.patch(`/${resource}/${editing.id}`, payload)
    } else {
      await api.post(`/${resource}`, payload)
    }
    setEditing(null)
    reload()
  }

  return (
    <div>
      <PageHeader
        eyebrow="Catalogue"
        title={title}
        action={
          <Button
            onClick={() => {
              setEditing({
                category: Object.keys(categoryMap)[0],
                isActive: true,
              } as Partial<TItem>)
            }}
          >
            + Nouveau
          </Button>
        }
      />
      <Card>
        <table className="w-full">
          <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-6 py-3" />
              <th className="px-6 py-3">Nom</th>
              <th className="px-6 py-3">Catégorie</th>
              <th className="px-6 py-3">Prix</th>
              <th className="px-6 py-3">Statut</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-border/50 last:border-0">
                <td className="px-3 py-2">
                  <img src={it.imageUrl} alt="" className="h-10 w-14 rounded object-cover" />
                </td>
                <td className="px-6 py-2 text-sm font-medium">{it.name}</td>
                <td className="px-6 py-2 text-sm text-muted-foreground">
                  {categoryMap[it.category]}
                </td>
                <td className="px-6 py-2 text-sm">
                  {formatPrice((it as unknown as Record<string, string>)[priceField])}
                </td>
                <td className="px-6 py-2">
                  <Badge variant={it.isActive ? 'success' : 'muted'}>
                    {it.isActive ? 'Actif' : 'Inactif'}
                  </Badge>
                </td>
                <td className="px-6 py-2 text-right">
                  <button
                    onClick={() => {
                      setEditing(it)
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    Éditer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => {
            setEditing(null)
          }}
        >
          <Card
            className="w-full max-w-xl"
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <CardHeader>
              <CardTitle>{editing.id ? 'Éditer' : 'Créer'}</CardTitle>
            </CardHeader>
            <form onSubmit={(e) => void save(e)}>
              <CardContent className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Slug</Label>
                    <Input
                      value={editing.slug ?? ''}
                      onChange={(e) => {
                        setEditing({ ...editing, slug: e.target.value })
                      }}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Catégorie</Label>
                    <Select
                      value={editing.category ?? ''}
                      onChange={(e) => {
                        setEditing({ ...editing, category: e.target.value })
                      }}
                    >
                      {Object.entries(categoryMap).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Nom</Label>
                  <Input
                    value={editing.name ?? ''}
                    onChange={(e) => {
                      setEditing({ ...editing, name: e.target.value })
                    }}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Textarea
                    rows={3}
                    value={editing.description ?? ''}
                    onChange={(e) => {
                      setEditing({ ...editing, description: e.target.value })
                    }}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Image</Label>
                  <ImageUpload
                    scope={resource === 'services' ? 'service' : 'product'}
                    value={editing.imageUrl}
                    onChange={(url) => {
                      setEditing({ ...editing, imageUrl: url })
                    }}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{priceField === 'basePrice' ? 'Prix de base' : 'Prix'}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={(editing as unknown as Record<string, number>)[priceField] ?? 0}
                      onChange={(e) => {
                        setEditing({ ...editing, [priceField]: parseFloat(e.target.value) })
                      }}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Devise</Label>
                    <Input
                      value={(editing as unknown as { currency?: string }).currency ?? 'EUR'}
                      onChange={(e) => {
                        setEditing({ ...editing, currency: e.target.value })
                      }}
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.isActive ?? true}
                    onChange={(e) => {
                      setEditing({ ...editing, isActive: e.target.checked })
                    }}
                  />{' '}
                  Actif
                </label>
                <div className="flex gap-2 pt-3">
                  <Button type="submit" className="flex-1">
                    {editing.id ? 'Mettre à jour' : 'Créer'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditing(null)
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </form>
          </Card>
        </div>
      )}
    </div>
  )
}

export function AdminServices(): ReactElement {
  return (
    <CatalogCrud<Service>
      title="Services"
      resource="services"
      categoryMap={SERVICE_CATEGORY_LABELS}
      priceField="basePrice"
    />
  )
}

export function AdminProducts(): ReactElement {
  return (
    <CatalogCrud<Product>
      title="Produits"
      resource="products"
      categoryMap={PRODUCT_CATEGORY_LABELS}
      priceField="price"
    />
  )
}

// =============================================================================
// Reviews moderation
// =============================================================================
export function AdminReviews(): ReactElement {
  const [items, setItems] = useState<
    (Review & {
      order?: { id: string; amount: string }
      client?: { name: string; email: string }
    })[]
  >([])
  function reload() {
    void api.get<{ data: typeof items }>('/reviews/admin/all').then((r) => {
      setItems(r.data.data)
    })
  }
  useEffect(reload, [])

  async function togglePublish(id: string, publish: boolean) {
    await api.patch(`/reviews/${id}/publish`, { publish })
    reload()
  }

  return (
    <div>
      <PageHeader eyebrow="Modération" title="Avis clients" />
      <div className="space-y-3">
        {items.map((r) => (
          <Card key={r.id}>
            <CardContent className="flex items-start justify-between gap-4 py-4">
              <div>
                <div className="flex items-center gap-3">
                  <span className="text-accent">
                    {'★'.repeat(r.rating)}
                    {'☆'.repeat(5 - r.rating)}
                  </span>
                  <span className="text-sm font-medium">{r.client?.name}</span>
                  <span className="text-xs text-muted-foreground">{formatDate(r.createdAt)}</span>
                </div>
                {r.comment && <p className="mt-2 text-sm">{r.comment}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge variant={r.isPublished ? 'success' : 'muted'}>
                  {r.isPublished ? 'Publié' : 'En attente'}
                </Badge>
                <Button
                  size="sm"
                  variant={r.isPublished ? 'outline' : 'default'}
                  onClick={() => void togglePublish(r.id, !r.isPublished)}
                >
                  {r.isPublished ? 'Dépublier' : 'Publier'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Aucun avis pour le moment.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

export type { Service, Product, Req as Request, Quote, Order, Review, User }
